import React, { useState, useEffect } from 'react';
import { API_URL } from '../components/config.js';
import axios from 'axios';
import HarvestingTopNav from '../components/HarvestingTopNav';
import './HarvestingInformationPage.css';

const HarvestingInformationPage = () => {
  const [resources, setResources] = useState([]);
  const [filteredResources, setFilteredResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Helper function to get absolute URL for file
  const getFileUrl = (url) => {
    if (!url) return '';
    // If URL already starts with http, return as is
    if (url.startsWith('http')) return url;
    // Otherwise, prepend API_URL
    return `${API_URL}${url}`;
  };
  const [selectedResource, setSelectedResource] = useState(null);

  const categories = [
    'All',
    'Permits & Licenses',
    'Regulations',
    'Guidelines',
    'Forms & Applications',
    'Seasonal Restrictions',
    'Bag Limits',
    'Protected Areas',
    'Safety Requirements',
    'Reporting Requirements',
    'Contact Information',
    'FAQs',
    'Updates & Changes',
    'Other'
  ];

  useEffect(() => {
    loadResources();
  }, []);

  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredResources(resources);
    } else {
      setFilteredResources(resources.filter(r => r.category === selectedCategory));
    }
  }, [selectedCategory, resources]);

  const loadResources = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/harvesting/information`);
      setResources(response.data.resources || []);
      setFilteredResources(response.data.resources || []);
    } catch (err) {
      console.error('Error loading information resources:', err);
      setError('Error loading resources');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Permits & Licenses': '📋',
      'Regulations': '⚖️',
      'Guidelines': '📖',
      'Forms & Applications': '📝',
      'Seasonal Restrictions': '📅',
      'Bag Limits': '🎯',
      'Protected Areas': '🛡️',
      'Safety Requirements': '⚠️',
      'Reporting Requirements': '📊',
      'Contact Information': '📞',
      'FAQs': '❓',
      'Updates & Changes': '🔔',
      'Other': '📄'
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return (
      <div className="harvesting-information-page">
        <div className="loading-spinner">Loading information...</div>
      </div>
    );
  }

  return (
    <div className="harvesting-information-page">
      <HarvestingTopNav />

      <div className="information-header">
        <h1>Harvesting Information Hub</h1>
        <p>Access guidelines, permits, regulations, and important information for sustainable harvesting</p>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Category Filter */}
      <div className="category-filter">
        <label htmlFor="category-select">
          <span className="filter-icon">⚙️</span> Filter by Category:
        </label>
        <select
          id="category-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="category-dropdown"
        >
          {categories.map(category => (
            <option key={category} value={category}>
              {category !== 'All' ? `${getCategoryIcon(category)} ${category}` : '📋 All Categories'}
            </option>
          ))}
        </select>
      </div>

      {/* Featured Resources */}
      {filteredResources.some(r => r.isFeatured) && (
        <div className="featured-section">
          <h2>Featured Information</h2>
          <div className="resources-grid">
            {filteredResources
              .filter(resource => resource.isFeatured)
              .map(resource => (
                <div key={resource._id} className="resource-card featured">
                  {resource.thumbnailUrl && (
                    <div className="resource-thumbnail">
                      <img src={resource.thumbnailUrl} alt={resource.title} />
                    </div>
                  )}
                  <div className="resource-content">
                    <div className="resource-category">
                      {getCategoryIcon(resource.category)} {resource.category}
                    </div>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    <div className="resource-meta">
                      <span>👁️ {resource.viewCount} views</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="resource-tags">
                          {resource.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn-view-resource"
                      onClick={() => setSelectedResource(resource)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* All Resources */}
      <div className="all-resources-section">
        <h2>All Information</h2>
        {filteredResources.filter(r => !r.isFeatured).length === 0 ? (
          <p className="no-resources">No information found in this category.</p>
        ) : (
          <div className="resources-grid">
            {filteredResources
              .filter(resource => !resource.isFeatured)
              .map(resource => (
                <div key={resource._id} className="resource-card">
                  {resource.thumbnailUrl && (
                    <div className="resource-thumbnail">
                      <img src={resource.thumbnailUrl} alt={resource.title} />
                    </div>
                  )}
                  <div className="resource-content">
                    <div className="resource-category">
                      {getCategoryIcon(resource.category)} {resource.category}
                    </div>
                    <h3>{resource.title}</h3>
                    <p>{resource.description}</p>
                    <div className="resource-meta">
                      <span>👁️ {resource.viewCount} views</span>
                      {resource.tags && resource.tags.length > 0 && (
                        <div className="resource-tags">
                          {resource.tags.slice(0, 3).map((tag, idx) => (
                            <span key={idx} className="tag">{tag}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <button
                      className="btn-view-resource"
                      onClick={() => setSelectedResource(resource)}
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Resource Detail Modal */}
      {selectedResource && (
        <div className="modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setSelectedResource(null); }}>
          <div className="modal-content large" onClick={(e) => {
            // Allow links to work properly
            if (e.target.tagName === 'A') {
              const href = e.target.getAttribute('href');
              if (href) {
                e.stopPropagation();
                e.preventDefault();
                
                if (href.startsWith('mailto:')) {
                  window.location.href = href;
                } else if (href.startsWith('tel:')) {
                  window.location.href = href;
                } else if (href.startsWith('http') || href.startsWith('//')) {
                  window.open(href, '_blank', 'noopener,noreferrer');
                } else {
                  window.location.href = href;
                }
              }
            } else {
              e.stopPropagation();
            }
          }}>
            <div className="modal-header">
              <h2>{selectedResource.title}</h2>
              <button className="modal-close" onClick={() => setSelectedResource(null)}>×</button>
            </div>
            <div className="modal-body">
              <div className="resource-detail-category">
                {getCategoryIcon(selectedResource.category)} {selectedResource.category}
              </div>
              
              {selectedResource.videoUrl && (
                <div className="resource-video">
                  <iframe
                    width="100%"
                    height="400"
                    src={selectedResource.videoUrl}
                    title={selectedResource.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              )}

              <div
                className="resource-detail-content"
                dangerouslySetInnerHTML={{ __html: selectedResource.content }}
              />

              {selectedResource.attachments && selectedResource.attachments.length > 0 && (
                <div className="resource-attachments">
                  <h4>📎 Attachments</h4>
                  {selectedResource.attachments.map((attachment, idx) => (
                    <div key={idx} className="attachment-item">
                      {attachment.fileType === 'application/pdf' ? (
                        <div className="pdf-viewer">
                          <div className="pdf-controls">
                            <span className="pdf-filename">📄 {attachment.filename}</span>
                            <a 
                              href={getFileUrl(attachment.url)} 
                              download={attachment.filename}
                              className="btn-download"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              ⬇ Download
                            </a>
                          </div>
                          <iframe
                            src={getFileUrl(attachment.url)}
                            width="100%"
                            height="600px"
                            title={attachment.filename}
                            style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                          />
                        </div>
                      ) : (
                        <div className="document-download">
                          <a 
                            href={getFileUrl(attachment.url)} 
                            download={attachment.filename}
                            className="btn-download-doc"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            📄 {attachment.filename} - Download ({(attachment.size / 1024).toFixed(2)} KB)
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {selectedResource.externalLink && (
                <div className="resource-external-link">
                  <a
                    href={selectedResource.externalLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-external-link"
                  >
                    🔗 Visit External Resource
                  </a>
                </div>
              )}

              {(selectedResource.effectiveDate || selectedResource.expiryDate) && (
                <div className="resource-dates">
                  {selectedResource.effectiveDate && (
                    <p><strong>Effective Date:</strong> {new Date(selectedResource.effectiveDate).toLocaleDateString()}</p>
                  )}
                  {selectedResource.expiryDate && (
                    <p><strong>Expiry Date:</strong> {new Date(selectedResource.expiryDate).toLocaleDateString()}</p>
                  )}
                </div>
              )}

              {selectedResource.tags && selectedResource.tags.length > 0 && (
                <div className="resource-detail-tags">
                  <strong>Tags:</strong>
                  {selectedResource.tags.map((tag, idx) => (
                    <span key={idx} className="tag">{tag}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HarvestingInformationPage;
